import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import type {
	IKompisTranslations,
	ILocalizedInformation,
	IStoreAvailability
} from '@inter-ikea-kompis/types';
import { AvailabilityUtility } from '@inter-ikea-kompis/utilities';
import type { RenderResult } from '@inter-ikea-kompis/web-component';

import { QrCode } from '@inter-ikea-kompis/component-qr-code';

import { Text } from '@ingka/common-styles-webc';
import { informationCircle, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import type { InlineMessageVariant } from '@ingka/inline-message-webc';
import { InlineMessage as SkapaInlineMessage } from '@ingka/inline-message-webc/passive.js';
import type { InputField as SkapaInputField } from '@ingka/input-field-webc/passive.js';

import type IBuyInStoreTranslations from '../../types/IBuyInStoreTranslations.js';
import BuyInStoreCardStyle from './BuyInStoreCardStyle.js';

/**
 * Card to show QR code in Kiosks. Used in BuyInStore.
 */

export default class BuyInStoreCard extends KompisElement {
	// Localized information. Deprecated - use "translations" instead.
	@property({ type: Object })
	public localizedInformation: ILocalizedInformation | null = null;

	// The translations retrieved from translations service.
	@property({ type: Object })
	public translations: IBuyInStoreTranslations | null = null;

	// A list of store availability objects.
	@property({ type: Array })
	public storeAvailabilities: IStoreAvailability[] | null = null;

	// Link used in the QR code to take the user to the summary page on their phone. This should link directly to the picking list of the summary page.
	@property({ type: String })
	public buyInStoreLink: string | null = null;

	public override async connectedCallback(): Promise<void> {
		registerIcon(informationCircle);

		this.customElements.define('skapa-inline-message', SkapaInlineMessage);
		this.customElements.define('skapa-icon', SkapaIcon);

		this.customElements.define('kompis-qr-code', QrCode);

		super.connectedCallback();
	}

	public override render(): RenderResult {
		// We want translations to be required, but since both props to provide translations are optional, this check is needed until localizedInformation is removed.
		if (!this.hasTranslations()) {
			// We can't throw an error because on the first render no props are set.
			return null;
		}

		return html`
			<style>
				${BuyInStoreCardStyle.getStyle()}
			</style>
			${this.getHeading()} ${this.getBody()} ${this.getQrCodeContainer()}
		`;
	}

	private hasTranslations(): boolean {
		return !!this.translations || !!this.localizedInformation?.translations;
	}

	private getTranslations(): IBuyInStoreTranslations {
		const translations = this.translations
			? this.translations
			: <IKompisTranslations>this.localizedInformation?.translations;

		return translations;
	}

	private getHeading(): RenderResult {
		return html`
			<h2 class="heading ${Text.classnames({ heading: { m: true } })}">
				${this.getTranslations().continueOnMobileHeading}
			</h2>
		`;
	}

	private getBody(): RenderResult {
		return html`
			<p class="body ${Text.classnames({ body: { m: true } })}">
				${this.getTranslations().continueOnMobileDescription}
			</p>

			${this.getFullServeMessage()}
		`;
	}

	private getFullServeMessage(): RenderResult {
		if (!this.showFullServeMessage()) {
			return null;
		}

		const variant: InlineMessageVariant = 'informative';

		return html`
			<skapa-inline-message subtle class="message" variant="${variant}">
				<skapa-icon slot="message-icon" icon="information-circle"></skapa-icon>
				<span slot="body">${this.getTranslations().buyInStoreFullServeMessage}</span>
			</skapa-inline-message>
		`;
	}

	private getQrCodeContainer(): RenderResult {
		return html`<div class="qr-code-container">
			${this.getQrCode()}
			<p class="qr-code-help-text ${Text.classnames({ body: { m: true } })}">
				${this.getTranslations().continueOnMobileQrCodeHelpText}
			</p>
		</div>`;
	}

	private getQrCode(): RenderResult {
		if (!this.buyInStoreLink) {
			return null;
		}

		const { buyInStoreQrCodeTitle } = this.getTranslations();

		return html`
			<div class="qr-code-background">
				<kompis-qr-code
					url="${this.buyInStoreLink}"
					qrCodeTitle="${buyInStoreQrCodeTitle}"
				></kompis-qr-code>
			</div>
		`;
	}

	private showFullServeMessage(): boolean {
		if (!this.storeAvailabilities) {
			return false;
		}

		return AvailabilityUtility.hasAnyFullServeAvailability(this.storeAvailabilities);
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'skapa-input-field': SkapaInputField;
		'skapa-inline-message': SkapaInlineMessage;
		'skapa-icon': SkapaIcon;
		'kompis-buy-in-store-card': BuyInStoreCard;
		'kompis-qr-code': QrCode;
	}
}
